import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  VStack,
  HStack,
  Heading,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Select,
  Input,
  Button,
  useColorModeValue,
  Spinner,
  Center,
  Icon,
  Code,
  Collapse,
  IconButton,
  useToast,
  Tooltip,
} from '@chakra-ui/react';
import { FiChevronDown, FiChevronUp, FiDownload, FiCopy, FiRefreshCw } from 'react-icons/fi';
import moment from 'moment';
import { supabase, isSupabaseConfigured } from '../../../utils/supabase';

const CHANNEL_COLORS = {
  sms: 'green',
  email: 'blue',
};

const STATUS_COLORS = {
  sent: 'green',
  delivered: 'green',
  pending: 'yellow',
  processing: 'yellow',
  failed: 'red',
  error: 'red',
  undelivered: 'red',
  rate_limited: 'orange',
  rejected_spam: 'red',
};

const SOURCE_LABELS = {
  bulk_sms: 'Bulk SMS',
  bulk_email: 'Bulk Email',
  automation: 'Automation',
  single: 'Single Send',
};

function escapeCSV(val) {
  if (val == null) return '';
  const str = String(val);
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

export default function CommunicationLog() {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [channelFilter, setChannelFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFilter, setDateFilter] = useState('7days');
  const [searchTerm, setSearchTerm] = useState('');

  const toast = useToast();
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const tableHeaderBG = useColorModeValue('gray.50', 'gray.800');
  const hoverBG = useColorModeValue('gray.50', 'gray.700');
  const expandedBG = useColorModeValue('blue.50', 'blue.900');

  const getDateStart = useCallback(() => {
    const now = new Date();
    switch (dateFilter) {
      case '24hours': return new Date(now.getTime() - 24 * 60 * 60 * 1000);
      case '7days': return new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      case '30days': return new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      default: return null;
    }
  }, [dateFilter]);

  const loadRecords = useCallback(async () => {
    if (!isSupabaseConfigured()) { setLoading(false); return; }
    setLoading(true);

    try {
      const merged = [];
      const dateStart = getDateStart();

      // --- SMS records from crm_dialpad_sms ---
      if (!channelFilter || channelFilter === 'sms') {
        let smsQuery = supabase
          .from('crm_dialpad_sms')
          .select('id, contact_id, user_id, dialpad_message_id, direction, content, from_number, to_number, status, sent_at, metadata, created_at, crm_contacts(first_name, last_name, email, phone)')
          .eq('direction', 'outbound')
          .order('sent_at', { ascending: false })
          .limit(200);

        if (dateStart) {
          smsQuery = smsQuery.gte('sent_at', dateStart.toISOString());
        }

        const { data: smsRows, error: smsErr } = await smsQuery;
        if (smsErr) console.error('SMS query error:', smsErr);

        (smsRows || []).forEach(row => {
          const meta = row.metadata || {};
          const contact = row.crm_contacts;
          const recipientName = contact ? [contact.first_name, contact.last_name].filter(Boolean).join(' ') : '';

          merged.push({
            id: `sms-${row.id}`,
            channel: 'sms',
            timestamp: row.sent_at || row.created_at,
            recipientName,
            recipientAddress: row.to_number || '',
            recipientEmail: contact?.email || '',
            recipientPhone: contact?.phone || '',
            senderUserId: row.user_id,
            senderName: meta.author_name || meta.authorName || '',
            status: row.status || 'unknown',
            source: meta.source || 'single',
            contentPreview: (row.content || '').substring(0, 120),
            contentFull: row.content || '',
            contactId: row.contact_id,
            dealId: null,
            dialpadMessageId: row.dialpad_message_id,
            queueTaskId: meta.queue_task_id || null,
            metadata: meta,
            error: null,
            errorType: null,
          });
        });
      }

      // --- Email records from crm_activities ---
      if (!channelFilter || channelFilter === 'email') {
        let emailQuery = supabase
          .from('crm_activities')
          .select('id, deal_id, author_id, type, content, metadata, created_at, crm_deals(contact_id, crm_contacts(first_name, last_name, email))')
          .in('type', ['email', 'email_sent', 'dialpad_sms_email'])
          .order('created_at', { ascending: false })
          .limit(200);

        if (dateStart) {
          emailQuery = emailQuery.gte('created_at', dateStart.toISOString());
        }

        const { data: emailRows, error: emailErr } = await emailQuery;
        if (emailErr) console.error('Email query error:', emailErr);

        (emailRows || []).forEach(row => {
          const meta = row.metadata || {};
          const contact = row.crm_deals?.crm_contacts;
          const recipientName = contact ? [contact.first_name, contact.last_name].filter(Boolean).join(' ') : '';

          merged.push({
            id: `email-${row.id}`,
            channel: 'email',
            timestamp: row.created_at,
            recipientName,
            recipientAddress: meta.to || contact?.email || '',
            recipientEmail: meta.to || contact?.email || '',
            recipientPhone: '',
            senderUserId: row.author_id,
            senderName: meta.authorName || '',
            status: meta.emailId ? 'sent' : 'logged',
            source: meta.source || 'single',
            contentPreview: (meta.subject || row.content || '').substring(0, 120),
            contentFull: row.content || '',
            subject: meta.subject || '',
            contactId: row.crm_deals?.contact_id || null,
            dealId: row.deal_id,
            emailId: meta.emailId || null,
            threadId: meta.threadId || null,
            metadata: meta,
            error: null,
            errorType: null,
          });
        });
      }

      // Sort by timestamp descending
      merged.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

      // Apply client-side filters
      let filtered = merged;

      if (sourceFilter) {
        filtered = filtered.filter(r => r.source === sourceFilter);
      }
      if (statusFilter) {
        filtered = filtered.filter(r => r.status === statusFilter);
      }
      if (searchTerm.trim()) {
        const lower = searchTerm.toLowerCase();
        filtered = filtered.filter(r =>
          (r.recipientName || '').toLowerCase().includes(lower) ||
          (r.recipientAddress || '').toLowerCase().includes(lower) ||
          (r.contentPreview || '').toLowerCase().includes(lower) ||
          (r.senderName || '').toLowerCase().includes(lower) ||
          (r.subject || '').toLowerCase().includes(lower)
        );
      }

      setRecords(filtered);
    } catch (err) {
      console.error('Error loading communication logs:', err);
      toast({ title: 'Error loading logs', description: err.message, status: 'error', duration: 4000 });
    } finally {
      setLoading(false);
    }
  }, [channelFilter, sourceFilter, statusFilter, dateFilter, searchTerm, getDateStart, toast]);

  useEffect(() => { loadRecords(); }, [loadRecords]);

  const toggleRow = (id) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // --- Debug Export (clipboard) ---
  const handleCopyDebugExport = () => {
    const lines = [
      '=== Origin CRM Communication Log Export ===',
      `Exported: ${new Date().toISOString()}`,
      `Filters: channel=${channelFilter || 'all'}, source=${sourceFilter || 'all'}, status=${statusFilter || 'all'}, date=${dateFilter}`,
      `Total records: ${records.length}`,
      '',
    ];

    records.forEach((r, i) => {
      lines.push(`--- Record ${i + 1} ---`);
      lines.push(`Channel: ${r.channel.toUpperCase()}`);
      lines.push(`Status: ${r.status}`);
      lines.push(`Timestamp: ${r.timestamp}`);
      lines.push(`Recipient: ${r.recipientName || 'N/A'} | ${r.recipientAddress || 'N/A'}`);
      lines.push(`Sender: ${r.senderName || 'N/A'} (uid: ${r.senderUserId || 'N/A'})`);
      if (r.channel === 'email' && r.subject) lines.push(`Subject: ${r.subject}`);
      lines.push(`Content: ${JSON.stringify(r.contentFull)}`);
      lines.push(`Source: ${r.source}`);
      if (r.contactId) lines.push(`Contact ID: ${r.contactId}`);
      if (r.dealId) lines.push(`Deal ID: ${r.dealId}`);
      if (r.dialpadMessageId) lines.push(`Dialpad Message ID: ${r.dialpadMessageId}`);
      if (r.queueTaskId) lines.push(`Queue Task ID: ${r.queueTaskId}`);
      if (r.emailId) lines.push(`Gmail ID: ${r.emailId}`);
      if (r.threadId) lines.push(`Thread ID: ${r.threadId}`);
      if (r.error) lines.push(`Error: ${r.error}`);
      if (r.errorType) lines.push(`Error Type: ${r.errorType}`);
      lines.push(`Metadata: ${JSON.stringify(r.metadata)}`);
      lines.push('');
    });

    navigator.clipboard.writeText(lines.join('\n'));
    toast({ title: 'Debug export copied to clipboard', description: `${records.length} records`, status: 'success', duration: 3000 });
  };

  // --- CSV Export ---
  const handleCSVExport = () => {
    if (records.length === 0) return;
    const headers = ['Timestamp', 'Channel', 'Status', 'Source', 'Recipient Name', 'Recipient Address', 'Sender', 'Subject', 'Content Preview', 'Contact ID', 'Deal ID', 'Dialpad Msg ID', 'Email ID', 'Error', 'Metadata'];
    const rows = records.map(r => [
      r.timestamp, r.channel, r.status, r.source,
      r.recipientName, r.recipientAddress, r.senderName,
      r.subject || '', r.contentPreview, r.contactId || '',
      r.dealId || '', r.dialpadMessageId || '', r.emailId || '',
      r.error || '', JSON.stringify(r.metadata),
    ].map(escapeCSV).join(','));

    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `comms-log-${moment().format('YYYY-MM-DD-HHmmss')}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <Center py={12}>
        <VStack spacing={3}>
          <Spinner size="lg" />
          <Text color="gray.500">Loading communication logs...</Text>
        </VStack>
      </Center>
    );
  }

  return (
    <VStack spacing={4} align="stretch">
      <HStack justify="space-between" flexWrap="wrap">
        <Heading size="md">Communication Log</Heading>
        <HStack spacing={2}>
          <Tooltip label="Copy all visible records as structured text for debugging">
            <Button size="sm" leftIcon={<FiCopy />} colorScheme="purple" variant="solid" onClick={handleCopyDebugExport} isDisabled={records.length === 0}>
              Copy Debug Export
            </Button>
          </Tooltip>
          <Button size="sm" leftIcon={<FiDownload />} variant="outline" onClick={handleCSVExport} isDisabled={records.length === 0}>
            CSV Export
          </Button>
          <IconButton size="sm" icon={<FiRefreshCw />} variant="ghost" onClick={loadRecords} aria-label="Refresh" />
        </HStack>
      </HStack>

      {/* Filters */}
      <HStack spacing={3} flexWrap="wrap">
        <Input placeholder="Search..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} size="sm" maxW="200px" />

        <Select placeholder="All Channels" value={channelFilter} onChange={e => setChannelFilter(e.target.value)} size="sm" maxW="150px">
          <option value="sms">SMS</option>
          <option value="email">Email</option>
        </Select>

        <Select placeholder="All Sources" value={sourceFilter} onChange={e => setSourceFilter(e.target.value)} size="sm" maxW="160px">
          <option value="single">Single Send</option>
          <option value="bulk_sms">Bulk SMS</option>
          <option value="bulk_email">Bulk Email</option>
          <option value="automation">Automation</option>
        </Select>

        <Select placeholder="All Statuses" value={statusFilter} onChange={e => setStatusFilter(e.target.value)} size="sm" maxW="150px">
          <option value="sent">Sent</option>
          <option value="delivered">Delivered</option>
          <option value="failed">Failed</option>
          <option value="pending">Pending</option>
          <option value="error">Error</option>
        </Select>

        <Select value={dateFilter} onChange={e => setDateFilter(e.target.value)} size="sm" maxW="150px">
          <option value="24hours">Last 24 Hours</option>
          <option value="7days">Last 7 Days</option>
          <option value="30days">Last 30 Days</option>
          <option value="all">All Time</option>
        </Select>

        {(channelFilter || sourceFilter || statusFilter || searchTerm || dateFilter !== '7days') && (
          <Button size="sm" variant="ghost" onClick={() => { setChannelFilter(''); setSourceFilter(''); setStatusFilter(''); setDateFilter('7days'); setSearchTerm(''); }}>
            Clear All
          </Button>
        )}

        <Badge colorScheme="gray" fontSize="sm" px={2} py={1}>{records.length} records</Badge>
      </HStack>

      {/* Table */}
      <Box overflowX="auto" borderWidth="1px" borderRadius="lg" borderColor={borderColor}>
        <Table variant="simple" size="sm">
          <Thead bg={tableHeaderBG}>
            <Tr>
              <Th width="40px"></Th>
              <Th>Timestamp</Th>
              <Th>Channel</Th>
              <Th>Recipient</Th>
              <Th>Sender</Th>
              <Th>Status</Th>
              <Th>Source</Th>
              <Th>Content</Th>
            </Tr>
          </Thead>
          <Tbody>
            {records.length === 0 ? (
              <Tr>
                <Td colSpan={8}>
                  <Center py={8}><Text color="gray.500">No communication records found</Text></Center>
                </Td>
              </Tr>
            ) : (
              records.map(r => (
                <React.Fragment key={r.id}>
                  <Tr _hover={{ bg: hoverBG }} cursor="pointer" onClick={() => toggleRow(r.id)}>
                    <Td>
                      <IconButton
                        size="xs"
                        variant="ghost"
                        icon={expandedRows.has(r.id) ? <FiChevronUp /> : <FiChevronDown />}
                        aria-label="Expand"
                      />
                    </Td>
                    <Td whiteSpace="nowrap" fontSize="xs">{moment(r.timestamp).format('MMM D, h:mm A')}</Td>
                    <Td><Badge colorScheme={CHANNEL_COLORS[r.channel] || 'gray'} fontSize="xs">{r.channel.toUpperCase()}</Badge></Td>
                    <Td fontSize="xs">
                      <Text fontWeight="medium">{r.recipientName || '-'}</Text>
                      <Text color="gray.500" fontSize="xs">{r.recipientAddress || '-'}</Text>
                    </Td>
                    <Td fontSize="xs">{r.senderName || '-'}</Td>
                    <Td><Badge colorScheme={STATUS_COLORS[r.status] || 'gray'} fontSize="xs">{r.status}</Badge></Td>
                    <Td fontSize="xs">{SOURCE_LABELS[r.source] || r.source}</Td>
                    <Td fontSize="xs" maxW="250px" isTruncated>
                      {r.channel === 'email' && r.subject ? <Text fontWeight="medium" isTruncated>{r.subject}</Text> : null}
                      <Text color="gray.600" isTruncated>{r.contentPreview}</Text>
                    </Td>
                  </Tr>
                  <Tr>
                    <Td colSpan={8} p={0} borderBottom={expandedRows.has(r.id) ? '1px solid' : 'none'} borderColor={borderColor}>
                      <Collapse in={expandedRows.has(r.id)} animateOpacity>
                        <Box bg={expandedBG} p={4} fontSize="xs">
                          <VStack align="stretch" spacing={2}>
                            {r.channel === 'email' && r.subject && (
                              <HStack><Text fontWeight="bold" minW="120px">Subject:</Text><Text>{r.subject}</Text></HStack>
                            )}
                            <HStack align="start"><Text fontWeight="bold" minW="120px">Full Content:</Text><Text whiteSpace="pre-wrap" maxH="200px" overflowY="auto">{r.contentFull}</Text></HStack>
                            <HStack><Text fontWeight="bold" minW="120px">Recipient:</Text><Text>{r.recipientName} | {r.recipientAddress}</Text></HStack>
                            <HStack><Text fontWeight="bold" minW="120px">Sender:</Text><Text>{r.senderName} (uid: {r.senderUserId})</Text></HStack>
                            <HStack><Text fontWeight="bold" minW="120px">Contact ID:</Text><Code fontSize="xs">{r.contactId || '-'}</Code></HStack>
                            <HStack><Text fontWeight="bold" minW="120px">Deal ID:</Text><Code fontSize="xs">{r.dealId || '-'}</Code></HStack>
                            {r.dialpadMessageId && <HStack><Text fontWeight="bold" minW="120px">Dialpad Msg ID:</Text><Code fontSize="xs">{r.dialpadMessageId}</Code></HStack>}
                            {r.queueTaskId && <HStack><Text fontWeight="bold" minW="120px">Queue Task ID:</Text><Code fontSize="xs">{r.queueTaskId}</Code></HStack>}
                            {r.emailId && <HStack><Text fontWeight="bold" minW="120px">Gmail ID:</Text><Code fontSize="xs">{r.emailId}</Code></HStack>}
                            {r.threadId && <HStack><Text fontWeight="bold" minW="120px">Thread ID:</Text><Code fontSize="xs">{r.threadId}</Code></HStack>}
                            {r.error && <HStack><Text fontWeight="bold" minW="120px" color="red.600">Error:</Text><Text color="red.600">{r.error}</Text></HStack>}
                            {r.errorType && <HStack><Text fontWeight="bold" minW="120px" color="red.600">Error Type:</Text><Badge colorScheme="red">{r.errorType}</Badge></HStack>}
                            <HStack align="start"><Text fontWeight="bold" minW="120px">Metadata:</Text><Code fontSize="xs" whiteSpace="pre-wrap" maxH="150px" overflowY="auto">{JSON.stringify(r.metadata, null, 2)}</Code></HStack>
                          </VStack>
                        </Box>
                      </Collapse>
                    </Td>
                  </Tr>
                </React.Fragment>
              ))
            )}
          </Tbody>
        </Table>
      </Box>
    </VStack>
  );
}
